import React from 'react';
import { useTranslation } from '../../i18n/i18n';
import { finite } from '../../utils/series';
import './ChartCard.scss';

const W = 600;
const H = 180;

interface ChartCardProps {
  title: string;
  unit: string;
  values: Array<number | null>;
  /** Tooltip label per point. */
  pointLabels: string[];
  /** Five labels spread along the x axis. */
  axisLabels: string[];
  decimals: number;
  kind: 'line' | 'bar';
  /** Fixed y axis [lo, hi]; without it the axis fits the data. */
  domain?: [number, number];
  hover: number | null;
  onHover: (index: number | null) => void;
}

const ChartCard = ({
  title,
  unit,
  values,
  pointLabels,
  axisLabels,
  decimals,
  kind,
  domain,
  hover,
  onHover,
}: ChartCardProps) => {
  const { t } = useTranslation('weather');
  const n = values.length;
  const present = finite(values);
  const hasData = present.length > 0;
  const min = hasData ? Math.min(...present) : 0;
  const max = hasData ? Math.max(...present) : 1;
  const pad = (max - min) * 0.15 || 1;
  // Series that cannot go negative (wind, rain, humidity) keep their axis at or above zero.
  const lo = domain ? domain[0] : kind === 'bar' ? 0 : min >= 0 ? Math.max(0, min - pad) : min - pad;
  const hi = domain ? domain[1] : max + pad;

  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const step = kind === 'bar' ? W / n : W / Math.max(n - 1, 1);
  const x = (i: number) => (kind === 'bar' ? (i + 0.5) * step : n === 1 ? W / 2 : i * step);

  let line = '';
  let area = '';
  let bars = '';
  if (kind === 'bar') {
    const bw = step * 0.62;
    values.forEach((v, i) => {
      if (v !== null && v > 0) {
        bars += `M${(x(i) - bw / 2).toFixed(1)} ${y(v).toFixed(1)}h${bw.toFixed(1)}V${H}h-${bw.toFixed(1)}Z`;
      }
    });
  } else {
    // Empty buckets break the line rather than being interpolated across.
    let segment: number[] = [];
    const flush = () => {
      if (!segment.length) return;
      const path = segment
        .map((i, k) => `${k ? 'L' : 'M'}${x(i).toFixed(1)} ${y(values[i] as number).toFixed(1)}`)
        .join('');
      line += segment.length === 1 ? `${path}h0.1` : path;
      area += `${path}L${x(segment[segment.length - 1]).toFixed(1)} ${H}L${x(segment[0]).toFixed(1)} ${H}Z`;
      segment = [];
    };
    values.forEach((v, i) => (v === null ? flush() : segment.push(i)));
    flush();
  }

  const showTip = hover !== null && hover < n && values[hover] !== null;
  const hv = showTip ? (values[hover as number] as number) : 0;
  const cx = showTip ? x(hover as number) : 0;
  const pct = (cx / W) * 100;
  const pctY = (y(hv) / H) * 100;
  const total = present.reduce((a, b) => a + b, 0);

  return (
    <article className="ChartCard">
      <div className="ChartCard__head">
        <h2>
          {title} <span>({unit})</span>
        </h2>
        {hasData && (
          <span className="ChartCard__range">
            {kind === 'bar'
              ? t('charts.total', { total: total.toFixed(decimals), unit: unit.split(' ')[0] })
              : t('charts.minMax', { min: min.toFixed(decimals), max: max.toFixed(decimals) })}
          </span>
        )}
      </div>
      <div className="ChartCard__body">
        <div aria-hidden="true" className="ChartCard__y">
          <span>{hasData ? hi.toFixed(decimals) : ''}</span>
          <span>{hasData ? ((hi + lo) / 2).toFixed(decimals) : ''}</span>
          <span>{hasData ? lo.toFixed(decimals) : ''}</span>
        </div>
        <div className="ChartCard__plot-col">
          <div className="ChartCard__plot" onMouseLeave={() => onHover(null)}>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={
                hasData
                  ? t('charts.aria', { title, min: min.toFixed(decimals), max: max.toFixed(decimals), unit })
                  : `${title}: ${t('charts.noData')}`
              }
            >
              <line
                x1="0"
                y1="0.5"
                x2={W}
                y2="0.5"
                stroke="#E3E8F0"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <line x1="0" y1="90" x2={W} y2="90" stroke="#E3E8F0" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <line
                x1="0"
                y1="179.5"
                x2={W}
                y2="179.5"
                stroke="#C9D2E0"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              {area && <path d={area} fill="#1F6FD1" fillOpacity="0.10" stroke="none" />}
              {line && (
                <path
                  d={line}
                  fill="none"
                  stroke="#1F6FD1"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {bars && <path d={bars} fill="#1F6FD1" stroke="none" />}
              {showTip && (
                <line
                  x1={cx}
                  y1="0"
                  x2={cx}
                  y2={H}
                  stroke="#0B1F3A"
                  strokeOpacity="0.45"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                />
              )}
              {values.map((_, i) => (
                <rect
                  key={i}
                  x={(x(i) - step / 2).toFixed(1)}
                  y="0"
                  width={step.toFixed(1)}
                  height={H}
                  fill="transparent"
                  onMouseEnter={() => hover !== i && onHover(i)}
                />
              ))}
            </svg>
            {!hasData && <div className="ChartCard__empty">{t('charts.noData')}</div>}
            {showTip && (
              <>
                <span aria-hidden="true" className="ChartCard__dot" style={{ left: `${pct}%`, top: `${pctY}%` }} />
                <div className="ChartCard__tip" style={{ left: `${Math.min(Math.max(pct, 12), 88)}%` }}>
                  <span>{pointLabels[hover as number]}</span>
                  <strong>
                    {hv.toFixed(decimals)} {unit}
                  </strong>
                </div>
              </>
            )}
          </div>
          <div aria-hidden="true" className="ChartCard__x">
            {axisLabels.map((l, i) => (
              <span key={i}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default ChartCard;
